import type { CartCheckoutPanelProps } from '@engyalo/delivery-ui-components'
import type { DefaultProps } from '../../../interfaces'
import { TextLibrary } from '../defaultConstants'

/**
 * Default props for the Entrega/Pago panel above the cart lines. Read-only
 * when it shipped (COM-798); since M2 each row also offers «Cambiar» where the
 * storefront authors a picker route for that rule (COM-1045,
 * `utils/cartCheckoutPanel.ts`).
 *
 * Every value here is either a text token or an icon glyph — no data. The
 * two data props, `expectedDeliveryDate` and `paymentMethod`, are injected
 * by `getCartCheckoutPanelProps` in `useCinnamonProps.ts` from the session's
 * `checkoutRules` selections. Nothing here reads Redux or the session, on
 * purpose: this file is the fleet-wide baseline a storefront's SCS body
 * merges on top of.
 *
 * ⚠️ **The two helper lines ship blank, except on a row with no «Cambiar»
 * (COM-1760).** `texts.cart.deliveryHelperText` and
 * `texts.cart.paymentHelperText` are `''` in every `defaultTexts/*.json`, and
 * the panel draws a helper only when its text is non-empty. The old copy said
 * the date and method were fixed, which is false on a row that offers
 * «Cambiar». The bindings below stay, so a storefront that authors
 * `texts.cart.*HelperText` in SCS shows its own copy on every row.
 *
 * On a filled row that is definitively without «Cambiar» (no picker route
 * for the rule, or auto-settle ended `'settled'` or `'empty'`) the date or
 * method really is fixed. There, `getCartCheckoutPanelProps`
 * (`utils/cartCheckoutPanel.ts`) replaces a helper that resolves to `''` with
 * `texts.cart.deliveryHelperTextReadOnly` / `paymentHelperTextReadOnly`.
 * Authored copy still wins. An authored `''` cannot be told apart from the
 * blank default, so such a storefront starts showing the read-only line; it
 * hides it by authoring the `…ReadOnly` key as `''`. While auto-settle is
 * still `'resolving'` or `'writing'` the row has no «Cambiar» either, but it
 * may get one back, so the configured helper stands and no read-only line is
 * drawn. The read-only copy is not bound here: the library declares no prop
 * for it, and an undeclared prop would land on the panel's wrapper as a DOM
 * attribute.
 *
 * ⚠️ **Copy in `defaultTexts/*.json` is a baseline, not a mandate.** A
 * storefront that clears the label to `''` loses the row entirely —
 * `CartCheckoutPanel`'s `rowShows` guard drops any row with a missing value
 * *or* a missing label (COM-852). That is the intended escape hatch for a
 * storefront that wants to hide one of the two rows without an SCS body
 * change.
 *
 * ⚠️ **No `paymentMethodLabels`.** That map is a tenant's word for each
 * backend payment enum value (`{ CASH: 'Efectivo contra entrega' }`), so it
 * cannot ship as fleet-wide copy — a wrong entry here would rename every
 * storefront's payment method. The panel renders an unmapped method raw,
 * which is visible and diagnosable rather than hidden.
 */
export const CartCheckoutPanelDefault: DefaultProps<CartCheckoutPanelProps> = {
  deliveryLabel: TextLibrary.CART_DELIVERY_LABEL,
  deliveryHelperText: TextLibrary.CART_DELIVERY_HELPER_TEXT,
  paymentLabel: TextLibrary.CART_PAYMENT_LABEL,
  paymentHelperText: TextLibrary.CART_PAYMENT_HELPER_TEXT,
  /*
   * The empty-row prompts, bound as of `@engyalo/delivery-ui-components`
   * `1.24.0` (COM-1184 PR 5a, commerce-ui-components #500). A storefront
   * with no picker route for a rule still has no action to pair with the
   * prompt — the library's `rowShowsEmpty` AND-drops the row when either
   * half is missing — so these defaults are inert on an unopted-in
   * storefront and the "zero visual change" story holds.
   */
  deliveryEmptyLabel: TextLibrary.CART_DELIVERY_EMPTY_LABEL,
  paymentEmptyLabel: TextLibrary.CART_PAYMENT_EMPTY_LABEL,
  /*
   * The library's default is `MdInventory`, closest resolving stand-in for
   * a delivery truck since neither the `md` nor the `cm` curated set carries
   * one (see the panel's own note in its `defaultProps`). Duplicated here
   * rather than pulled from the library because cinnamon owns the token
   * seam — a tenant overriding the glyph does so on this prop, not by
   * shipping a new library version.
   */
  deliveryIcon: 'MdInventory',
  paymentIcon: 'MdOutlinePayments',
  iconset: 'md',
}

export default CartCheckoutPanelDefault
